// cluster/components/form/Form.tsx
import React, { SetStateAction } from 'react'
import { Button } from '../../../../../common/components/button/Button'
import { Input } from '../../../../../common/components/input/Input'
import { Textarea } from '../../../../../common/components/textarea/Textarea'
import { ClusterPayload } from '../../../../../common/types/interfaces'
import classes from './Form.module.sass'
import { useForm } from './hooks/useForm'
import { Schedule } from '../../../../../common/components/schedule/Schedule'
import { ClusterFormView, ClusterTypes } from '../../../../../common/types/enums'
import { SettingsForm } from '../settingsForm/SettingsForm'
import { SearchSelect } from '../../../../../common/components/searchSelect/SearchSelect'
import { LegalEntityType } from '../../../../../common/types/enums'
import { createTypeSelectList } from '../../../../../common/utils/functions/createTypeSelectList'
import {
  legalEntityTypeToText,
  clusterTypeToText,
} from '../../../../../common/utils/functions/enumToText'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '../../../../../store/slices/currentUserSlice'
import { chatAdminId } from '../../../../../common/utils/constants'
import { TariffForm } from '../tariffForm/TariffForm'
import { Toggle } from '../../../../../common/components/newToggle/Toggle'

type Props = {
  edit: boolean
  data: ClusterPayload
  setData: React.Dispatch<SetStateAction<ClusterPayload>>
  getList: () => void
  setModalActive: React.Dispatch<SetStateAction<boolean>>
  el?: ClusterPayload
}

export const Form = ({ edit, data, setData, getList, setModalActive, el }: Props) => {
  const {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    toggleAction,
    view,
    setView,
    setSettingsDisabled,
    toggleHandler,
    isButtonDisabled,
    paymentHandler,
    cdekHandler,
    cdekMarkupHandler,
  } = useForm(data, setData, edit, getList, setModalActive, el)

  const currentUser = useSelector(selectCurrentUser)

  return (
    <div className={classes.container}>
      <div className={classes.bookMarks}>
        <div
          className={view === ClusterFormView.FORM ? classes.titleActive : classes.title}
          onClick={() => setView(ClusterFormView.FORM)}
        >
          {data.id ? 'редактирование' : 'добавление'}
        </div>

        {data.withDelivery && (
          <div
            className={view === ClusterFormView.DELIVERY ? classes.titleActive : classes.title}
            onClick={() => setView(ClusterFormView.DELIVERY)}
          >
            {'доставка'}
          </div>
        )}
        {data.withDelivery && (
          <div
            className={view === ClusterFormView.CDEK ? classes.titleActive : classes.title}
            onClick={() => setView(ClusterFormView.CDEK)}
          >
            {'сдэк'}
          </div>
        )}

        {data.withBonuses && (
          <div
            className={view === ClusterFormView.BONUS ? classes.titleActive : classes.title}
            onClick={() => setView(ClusterFormView.BONUS)}
          >
            {'бонусы'}
          </div>
        )}

        <div
          className={view === ClusterFormView.PAYMENT ? classes.titleActive : classes.title}
          onClick={() => setView(ClusterFormView.PAYMENT)}
        >
          {'оплата'}
        </div>

        {currentUser?.id === chatAdminId && (
          <div
            className={view === ClusterFormView.TARIFF ? classes.titleActive : classes.title}
            onClick={() => setView(ClusterFormView.TARIFF)}
          >
            {'тариф'}
          </div>
        )}
      </div>

      {view === ClusterFormView.FORM && (
        <div className={classes.groupFormContainer}>
          {currentUser?.id === chatAdminId && (
            <Input
              type={'text'}
              fieldName={'domain'}
              value={data.domain || ''}
              placeHolder={'Домен кластера'}
              onChangeHandler={inputHandler}
              itemValidationResult={getValidationResult('domain')}
              onBlur={setDirty}
            />
          )}
          <Input
            type={'text'}
            fieldName={'name'}
            value={data.name}
            placeHolder={'Название кластера'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('name')}
            onBlur={setDirty}
          />
          <Input
            type={'text'}
            fieldName={'phone_number'}
            value={data.phone_number}
            placeHolder={'Телефон горячей линии'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('phone_number')}
            onBlur={setDirty}
          />

          <SearchSelect
            onBlur={setDirty}
            fieldName="type"
            options={createTypeSelectList(ClusterTypes, clusterTypeToText)}
            value={data.type}
            onChangeHandler={inputHandler}
            type="text"
            placeholder="Выберите тип кластера"
            itemValidationResult={getValidationResult('type')}
          />

          <Textarea
            type={'textarea'}
            fieldName={'description'}
            value={data.description}
            placeHolder="Опишите кластер подробно"
            rows={3}
            cols={20}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('description')}
            onBlur={setDirty}
          />

          {/* ЮРИДИЧЕСКИЕ ДАННЫЕ */}
          <div className={classes.legalSection}>
            <div className={classes.sectionTitle}>Юридические данные</div>

            <SearchSelect
              onBlur={setDirty}
              fieldName="legalEntityType"
              options={createTypeSelectList(LegalEntityType, legalEntityTypeToText)}
              value={data.legalEntityType}
              onChangeHandler={inputHandler}
              type="text"
              placeholder="Выберите организационно-правовую форму"
              itemValidationResult={getValidationResult('legalEntityType')}
            />

            <Input
              type={'text'}
              fieldName={'legalName'}
              value={data.legalName || ''}
              placeHolder={'Название организации'}
              onChangeHandler={inputHandler}
              itemValidationResult={getValidationResult('legalName')}
              onBlur={setDirty}
            />

            <Input
              type={'text'}
              fieldName={'legalInn'}
              value={data.legalInn || ''}
              placeHolder={'ИНН'}
              onChangeHandler={inputHandler}
              itemValidationResult={getValidationResult('legalInn')}
              onBlur={setDirty}
            />

            <Input
              type={'text'}
              fieldName={'legalAddress'}
              value={data.legalAddress || ''}
              placeHolder={'Юридический адрес'}
              onChangeHandler={inputHandler}
              itemValidationResult={getValidationResult('legalAddress')}
              onBlur={setDirty}
            />

            <Input
              type={'text'}
              fieldName={'agreement'}
              value={data.agreement || ''}
              placeHolder={'Договор (номер и дата)'}
              onChangeHandler={inputHandler}
              itemValidationResult={getValidationResult('agreement')}
              onBlur={setDirty}
            />
          </div>

          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => toggleAction('forceStore')}
              checked={Boolean(data.forceStore)}
            />
            <div>Принудительный выбор точки</div>
          </div>

          {data.forceStore && (
            <div className={classes.toggleContainer}>
              <Toggle onChange={() => toggleAction('onePrice')} checked={Boolean(data.onePrice)} />
              <div>Единое ценообразование</div>
            </div>
          )}

          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => toggleAction('showOutOfStock')}
              checked={Boolean(data.showOutOfStock)}
            />
            <div>Показывать отсутствующий товар</div>
          </div>

          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => toggleAction('manageStock')}
              checked={Boolean(data.manageStock)}
            />
            <div>Бронировать и списывать остатки</div>
          </div>

          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => toggleAction('showRating')}
              checked={Boolean(data.showRating)}
            />
            <div>Показывать рейтинг товаров и услуг</div>
          </div>

          <div className={classes.toggleContainer}>
            <Toggle onChange={() => toggleAction('withDelivery')} checked={data.withDelivery} />
            <div>Есть доставка</div>
          </div>

          {data.withDelivery && (
            <div className={classes.toggleContainer}>
              <Toggle
                onChange={() => toggleAction('autoCreateDeliveryTasks')}
                checked={Boolean(data.autoCreateDeliveryTasks)}
              />
              <div>Автоматически создавать задания на доставку</div>
            </div>
          )}

          <div className={classes.toggleContainer}>
            <Toggle onChange={() => toggleAction('withBonuses')} checked={data.withBonuses} />
            <div>Бонусная система</div>
          </div>

          {Number(data.type) === ClusterTypes.FOOD && (
            <div className={classes.toggleContainer}>
              <Toggle onChange={() => toggleAction('cutlery')} checked={Boolean(data.cutlery)} />
              <div>Приборы</div>
            </div>
          )}

          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => toggleAction('regularSales')}
              checked={data.regularSales}
              disabled={!data.complexSales && data.regularSales}
            />
            <div>Обычные продажи</div>
          </div>

          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => toggleAction('complexSales')}
              checked={data.complexSales}
              disabled={!data.regularSales && data.complexSales}
            />
            <div>Комплексные продажи</div>
          </div>

          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => toggleAction('isActive')}
              checked={Boolean(data.isActive)}
              disabled={currentUser?.id !== chatAdminId}
            />
            <div>Активен</div>
          </div>
        </div>
      )}

      {view === ClusterFormView.DELIVERY && (
        <div className={classes.groupFormContainer}>
          {data.schedule && data.withDelivery && (
            <Schedule data={data} title="НАСТРОЙКА РЕЖИМА РАБОТЫ" setData={setData} />
          )}
          <div className={classes.titleActive}>настройка стоимости</div>
          <SettingsForm
            data={data.delivery}
            setData={setData}
            fieldName={'delivery'}
            placeHolders={['Порог от, руб', 'Стоимость, руб']}
            setDisabled={setSettingsDisabled}
          />
        </div>
      )}

      {view === ClusterFormView.CDEK && (
        <div className={classes.groupFormContainer}>
          <div className={classes.titleActive}>настройки СДЭК</div>

          <div className={classes.toggleContainer}>
            <Toggle onChange={() => cdekHandler('enabled')} checked={data.cdek?.enabled || false} />
            <div>Включить доставку СДЭК</div>
          </div>

          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => cdekHandler('insurance')}
              checked={data.cdek?.insurance || false}
            />
            <div>Страхование заказов</div>
          </div>

          <div className={classes.toggleContainer}>
            <Toggle onChange={() => cdekHandler('pickup')} checked={data.cdek?.pickup || false} />
            <div>Доставка ПВЗ - ПВЗ</div>
          </div>

          <div className={classes.toggleContainer}>
            <Toggle onChange={() => cdekHandler('courier')} checked={data.cdek?.courier || false} />
            <div>Курьерская доставка или ПВЗ - курьер</div>
          </div>
          <Input
            label="Наценка на доставку от -100 до 999 процентов"
            type={'text'}
            fieldName={'cdekMarkup'}
            value={data.cdek?.markup ?? 0}
            placeHolder={'Наценка, %'}
            onChangeHandler={(value) => cdekMarkupHandler(String(value))}
            onBlur={setDirty}
          />
        </div>
      )}

      {view === ClusterFormView.BONUS && data.withBonuses && (
        <div className={classes.groupFormContainer}>
          <div className={classes.titleActive}>настройка бонусной системы</div>
          <div className={classes.titleActive}>% списания</div>
          <Input
            type={'text'}
            fieldName={'writeOff'}
            value={data.writeOff || ''}
            placeHolder={'% списания 1-100'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('writeOff')}
            onBlur={setDirty}
          />
          <div className={classes.titleActive}>пороги накопления</div>
          <SettingsForm
            data={data.bonuses}
            setData={setData}
            fieldName={'bonuses'}
            placeHolders={['Накопления, руб', '% начисления']}
            setDisabled={setSettingsDisabled}
          />
        </div>
      )}

      {view === ClusterFormView.PAYMENT && (
        <div className={classes.groupFormContainer}>
          <div className={classes.titleActive}>настройки оплаты</div>

          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => toggleHandler('yookassa')}
              checked={data.yookassa?.enabled || false}
            />
            <div>Онлайн оплата через ЮKassa</div>
          </div>
          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => toggleHandler('paymaster')}
              checked={data.paymaster?.enabled || false}
            />
            <div>Онлайн оплата через Paymaster</div>
          </div>

          <div className={classes.titleActive}>при доставке</div>
          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => paymentHandler('cashOnDelivery')}
              checked={data.payment?.cashOnDelivery ?? true}
            />
            <div>Наличными при получении</div>
          </div>
          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => paymentHandler('cardOnDelivery')}
              checked={data.payment?.cardOnDelivery ?? true}
            />
            <div>Картой при получении</div>
          </div>
          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => paymentHandler('onlineOnDelivery')}
              checked={data.payment?.onlineOnDelivery ?? true}
            />
            <div>Онлайн</div>
          </div>
          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => paymentHandler('billOnDelivery')}
              checked={data.payment?.billOnDelivery ?? true}
            />
            <div>По выставленному счету</div>
          </div>

          <div className={classes.titleActive}>при самовывозе</div>
          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => paymentHandler('cashOnPickup')}
              checked={data.payment?.cashOnPickup ?? true}
            />
            <div>Наличными при получении</div>
          </div>
          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => paymentHandler('cardOnPickup')}
              checked={data.payment?.cardOnPickup ?? true}
            />
            <div>Картой при получении</div>
          </div>
          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => paymentHandler('onlineOnPickup')}
              checked={data.payment?.onlineOnPickup ?? true}
            />
            <div>Онлайн</div>
          </div>
          <div className={classes.toggleContainer}>
            <Toggle
              onChange={() => paymentHandler('billOnPickup')}
              checked={data.payment?.billOnPickup ?? true}
            />
            <div>По выставленному счету</div>
          </div>
        </div>
      )}

      {view === ClusterFormView.TARIFF && currentUser?.id === chatAdminId && (
        <div className={classes.groupFormContainer}>
          <TariffForm tariff={data.tariff} setTariff={(tariff) => setData({ ...data, tariff })} />
        </div>
      )}

      <Button
        text={
          view === ClusterFormView.FORM && edit
            ? 'Сохранить'
            : view === ClusterFormView.FORM && !edit
              ? 'Добавить'
              : 'К сохранению'
        }
        onClick={() => {
          if (view === ClusterFormView.FORM) {
            formAction()
            return
          }
          setView(ClusterFormView.FORM)
        }}
        disabled={isButtonDisabled}
      />
    </div>
  )
}
